import { Autocomplete, Box, Button, Divider, Grid, Paper, TextField, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import ControlledDatePicker from 'src/shared/components/form/controlledDatePicker';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { District } from 'src/shared/models/district/district';
import { Association } from 'src/shared/models/association/association';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { ControlledCheckboxNew } from 'src/shared/components/form/controlledCheckboxNew';
import { AssociationAndDistrictSelect } from 'src/shared/components/organisms/associationAndDistrictSelect';
import { useGlobals } from 'src/shared/contexts/appContext';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { getAllMemberCount } from 'src/shared/services/statisticsService';
import { InfoOutlined } from '@mui/icons-material';
import { useFetchSnapshots } from '../hooks/useFetchSnapshots';
import { SnapshotTimeResponse } from '../models/snapshotTimeResponse';
import { MemberCountSearchFilter } from '../models/memberCountSearchFilter';
import { MembersCount } from '../models/membersCount';
import { StatisticsMemberCountDataGrid } from '../components/statisticsMemberCountDataGrid';

export const StatisticsMemberCountPage = () => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();

  const [association, setAssociation] = useState<Association>();
  const [district, setDistrict] = useState<District>();
  const { spans } = useFetchSnapshots();

  const [dataList, setDataList] = useState<MembersCount[]>();
  const [loading, setLoading] = useState<boolean>(false);

  const schema: yup.Schema<MemberCountSearchFilter> = yup.object().shape({
    snapshotTimeId: yup.number().optional(),
    expireDate: yup.string().required(t('common:validationMessages.required')),
    uniqueByPersonalIdentityNumber: yup.boolean().required(),
    uniqueByFirstNameLastNameAndEmail: yup.boolean().required(),
    associationId: yup.number().optional(),
    districtId: yup.number().optional(),
  });

  const formMethods = useForm<MemberCountSearchFilter>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      snapshotTimeId: undefined,
      expireDate: '',
      uniqueByPersonalIdentityNumber: false,
      uniqueByFirstNameLastNameAndEmail: false,
      associationId: undefined,
      districtId: undefined,
    },
  });

  const { handleSubmit, watch, setValue } = formMethods;

  const snapshotTimeId = watch('snapshotTimeId');

  const selectedSnapshot = spans.find((o) => o.snapshotTime_id === snapshotTimeId) ?? null;

  const handleFromDateChange = (_e, newValue) => {
    if (newValue) {
      setValue('snapshotTimeId', newValue.snapshotTime_id);
    } else {
      setValue('snapshotTimeId', undefined);
    }
  };

  const fetchData = useCallback(
    async (data: MemberCountSearchFilter) => {
      setLoading(true);

      const dataToFetch: MemberCountSearchFilter = {
        ...data,
        expireDate: DateHelper.getFSDate(data.expireDate),
      };

      try {
        const { data: rows } = await getAllMemberCount(dataToFetch);
        if (isMounted()) setDataList(rows);
      } catch (e) {
        ConsoleHelper.log(e);
      } finally {
        setLoading(false);
      }
    },
    [isMounted],
  );

  const isOptionEqualToValue = (option: SnapshotTimeResponse, value: SnapshotTimeResponse) => option.snapshotTime_id === value.snapshotTime_id;

  const formId = 'search-member-count-id';
  return (
    <FormProvider {...formMethods}>
      <form id={formId} onSubmit={handleSubmit(fetchData)}>
        <Grid container spacing={3}>
          <Grid item xs>
            <MainContentHeader title={t('statistics:report.membersCount')} />
          </Grid>
          <Grid
            item
            xs='auto'
            sx={{
              pt: 2,
              pl: 2,
              display: { sm: 'flex' },

              '& .MuiAutocomplete-inputRoot': {
                backgroundColor: 'white',
              },
            }}
          >
            <AssociationAndDistrictSelect
              districtMb={2}
              hideDistrict={currentUser?.districts?.length === 0}
              association={association}
              district={district}
              associationCallback={(val) => {
                setValue('associationId', val?.localAss_id || undefined);
                setAssociation(val || undefined);
              }}
              districtCallback={(val) => {
                setValue('districtId', val?.countyAss_id || undefined);
                setDistrict(val || undefined);
              }}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} mt={3}>
          <Paper sx={{ p: 2 }}>
            <Grid container>
              <LoadingOverlay loading={loading} />
              <Grid container spacing={3} mb={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete<SnapshotTimeResponse>
                    value={selectedSnapshot}
                    isOptionEqualToValue={isOptionEqualToValue}
                    getOptionLabel={(option) => DateHelper.getDate(option.snapshotTime) || ''}
                    options={spans}
                    onChange={handleFromDateChange}
                    aria-required
                    renderOption={(props, option) => (
                      <li {...props} key={option.snapshotTime_id}>
                        {DateHelper.getDate(option.snapshotTime)}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t('common:button.choose')}
                        fullWidth
                        label={t('statistics:membersCount.time')}
                        size='small'
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box display='flex' flexDirection='row' gap={2}>
                    <ControlledDatePicker name='expireDate' label={t('statistics:membersCount.expireDate')} />
                    <Tooltip title={t('statistics:membersCount.expireDateInfo')} arrow placement='top'>
                      <InfoOutlined fontSize='small' sx={{ color: 'text.secondary', mt: 1 }} />
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <ControlledCheckboxNew label={t('statistics:membersCount.uniqueMembersSsnNumber')} name='uniqueByPersonalIdentityNumber' />
                <ControlledCheckboxNew label={t('statistics:membersCount.uniqueMembersNameEmail')} name='uniqueByFirstNameLastNameAndEmail' />
                <Divider sx={{ mt: 2, mb: 2 }} />
              </Grid>
              <Grid container spacing={2}>
                <Grid item>
                  <Button form={formId} type='submit' variant='contained'>
                    {t('common:button.search')}
                  </Button>
                </Grid>
              </Grid>
              {dataList && (
                <Grid item xs={12}>
                  <Divider sx={{ mt: 2, mb: 2 }} />
                  <StatisticsMemberCountDataGrid dataList={dataList} />
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      </form>
    </FormProvider>
  );
};
